import React, {
  useCallback, useEffect, useMemo, useState, ReactElement, Fragment,
} from 'react';
import { connect, useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams, Navigate } from 'react-router';
import CheckAccess from '../../../components/check-access/check-access.component';
import ContentHeader from '../../../components/content-header/content-header.component';
import StatusBar from '../../../components/status-bar/status-bar.component';
import Loader from '../../../components/loader/loader.component';
import PageTabs from '../../../components/page-tabs/page-tabs.component';
import PullToRefresh from '../../../components/pull-to-refresh/pull-to-refresh.component';
import LeaveConfirmModal from '../../../components/leave-confirm-modal/leave-confirm-modal.component';
import Form from '../../../components/form/form.component';
import FormError from '../../../components/form-error/form-error.component';
import FormContainer from '../../../components/form-container/form-container.component';
import Controls from '../../app/controls/controls.component';
import SettingsPageTabs from '../settings-page-tabs/settings-page-tabs.component';
import SettingsPortfolioSummary from './settings-portfolio-summary/settings-portfolio-summary.component';
import SettingsPortfolioValidator from './settings-portfolio.validator';
import FormatService from '../../../services/format-service';
import ScreenService from '../../../services/screen-service';
import FormService from '../../../services/form-service';
import useServerFormErrors from '../../../hooks/use-server-form-errors';
import useEditModeQuery from '../../../hooks/use-edit-mode-query';
import useFormBlock from '../../../hooks/use-form-block';
import useFormStructureModified from '../../../hooks/use-form-structure-modified';
import {
  getPortfolioDealEmailConnectorData, resetDealConnectorAuthLink,
} from '../settings-portfolio-group/settings-portfolio-group.action';
import { getPortfolioData, savePortfolioData } from './settings-portfolio.action';
import { toggleEditMode } from '../../app/controls/controls.action';
import { resetFormErrors } from '../../app/app.action';
import { PORTFOLIO_EDIT } from '../../../constants/entity/permissions';
import { FORM_ID_SETTINGS_PORTFOLIO } from '../../../constants/system/form-ids';
import { ROUTE_NOT_FOUND, ROUTE_PARAM_ID, ROUTE_PORTFOLIO } from '../../../constants/system/routes';
import { TAB_NAME_PORTFOLIO_MANAGEMENT, TAB_NAME_SUMMARY } from '../../../constants/system/tab-names';
import { TAB_INDEX_DEFAULT } from '../../../constants/system/tab-indexes';
import { QUERY_PARAM_TAB } from '../../../constants/system/query-params';
import { MARKER_COLOR_RED } from '../../../constants/entity/marker-colors';

const SettingsPortfolio: ReactMemoComponent = React.memo((props: ReactComponentProps): ReactElement => {
  const {
    isEditMode,
    isDataLoaded,
    isRightDrawerOpen,
    currentUser,
    portfolioData,
    portfolioMarkerColorList,
    portfolioGroupList,
    dealConnectorAuthLink,
    form: formProps,
    modified: formModifiedFields,
    handleSubmit: submitForm,
    pristine: isFormPristine,
    isSubmitting: isFormSaving,
    error: formError,
    values: formValues,
    hasValidationErrors: withFormErrors,
    formErrors: serverFormErrors,
    mainFormError: mainServerFormError,
  } = props;

  const {
    reset: resetForm,
  } = formProps;

  const routeParams: ReactRouterParams = useParams();
  const navigate: ReactRouterNavigate = useNavigate();
  const dispatch: ReduxDispatch = useDispatch();
  const isFormStructureModified: boolean = useFormStructureModified(formModifiedFields, isDataLoaded);

  const portfolioId: number = FormatService.parseInt(routeParams[ROUTE_PARAM_ID]);
  const locationQueryParams: ParsedQueryParams | null = FormatService.parseQueryParams(location.search);
  const queryTab: string | null = locationQueryParams && locationQueryParams[QUERY_PARAM_TAB]
    ? FormatService.parseStringParam(locationQueryParams[QUERY_PARAM_TAB].toString()) : null;
  const withServerErrors: boolean = serverFormErrors && !!Object.keys(serverFormErrors).length;

  const [isOpenMessage, setIsOpenMessage]: [boolean, Function] = useState(false);
  const [statusMessage, setStatusMessage]: [string, Function] = useState('');
  const [isLeaveConfirmed, setIsLeaveConfirmed]: [boolean, Function] = useState(false);
  const [isLeaveConfirmModalOpen, setIsLeaveConfirmModalOpen]: [boolean, Function] = useState(false);
  const [lastLocation, setLastLocation]: [ReactRouterLocation | null, Function] = useState<StateValueDefault>(null);
  const [lastTabIndex, setLastTabIndex]: [number | null, Function] = useState(null);

  const pageTitle: string = `Portfolio${portfolioData ? `: ${portfolioData.name}` : ''}`;

  const portfolioEditPermissions: string[] = useMemo((): string[] => [PORTFOLIO_EDIT], []);

  const isFormDataPristine: boolean = isFormPristine;
  const isFormChanged: boolean = isEditMode && !isFormDataPristine;
  const isFormStateChanged: boolean = isEditMode;
  const isSaveDisabled: boolean = isFormSaving || (isFormDataPristine && !isFormStructureModified);
  const isEditModeQueryDisabled: boolean = isLeaveConfirmed && (!!lastLocation || (lastTabIndex !== null
    && lastTabIndex >= TAB_INDEX_DEFAULT));

  const onCancelForm: Function = useCallback((): void => {
    FormService.forceResetForm(resetForm, formProps);

    dispatch(toggleEditMode(false));
  }, []);

  const onLeaveConfirm: Function = useCallback((): void => {
    setIsLeaveConfirmed(true);
    setIsLeaveConfirmModalOpen(false);

    onCancelForm();
  }, []);

  const closeLeaveConfirmModal: Function = useCallback((): void => {
    setIsLeaveConfirmModalOpen(false);
  }, []);

  const closeMessage: Function = useCallback((): void => {
    setIsOpenMessage(false);
    setStatusMessage('');
  }, []);

  const onFormLeave: FormLeaveFunction = useCallback((nextLocation: ReactRouterLocation): boolean => {
    if (isFormChanged) {
      setIsLeaveConfirmModalOpen(true);

      setLastLocation(nextLocation);

      return false;
    }

    if (isEditMode) {
      dispatch(toggleEditMode(false));
    }

    return true;
  }, [isFormChanged, isEditMode]);

  const onBeforeLocationChange: Function = useCallback((nextLocation: ReactRouterLocation): void => {
    if (isFormChanged) {
      setIsLeaveConfirmModalOpen(true);

      setLastLocation(nextLocation);
    }
  }, [isFormChanged]);

  const sendForm: Function = useCallback((values: FormValues): Promise<ActionCallback> | null => {
    if (!values) {
      return null;
    }

    const portfolioItem: FormValueItem = {
      ...values,
    };

    portfolioItem.capitalAllocated = portfolioItem.capitalAllocated
      ? FormatService.formatCleanNumber(portfolioItem.capitalAllocated) : 0;

    return new Promise((resolve: ActionResolveCallback): void => {
      dispatch(savePortfolioData({
        portfolio: portfolioItem,
      }, (response: ActionResponse): void => {
        onCancelForm();

        setStatusMessage('Saved successfully');
        setIsOpenMessage(true);

        resolve(response);
      }, (exception: ServerError): void => {
        if (exception && exception.message) {
          submitForm();
        }
      }));
    });
  }, [isSaveDisabled]);

  const onSubmitForm: Function = useCallback((): void => {
    submitForm();

    if (!withFormErrors) {
      sendForm(formValues);
    } else {
      FormService.focusOnFirstFormError();
    }
  }, [isSaveDisabled, formValues, withFormErrors]);

  const onRefresh: Function = useCallback((): void => {
    if (portfolioId) {
      dispatch(getPortfolioData({
        portfolioId,
      }));
    }
  }, [portfolioId]);

  const dealConnectEmailClick: Function = useCallback((): void => {
    if (portfolioId) {
      dispatch(getPortfolioDealEmailConnectorData({
        portfolioId,
      }));
    }
  }, [portfolioId]);

  const tabs: TabItem[] = [
    {
      id: 1,
      name: TAB_NAME_SUMMARY,
      title: 'Summary',
      component: (
        <SettingsPortfolioSummary
          isSaving={isFormSaving}
          isSaveDisabled={isSaveDisabled}
          isEditMode={isEditMode}
          isRightDrawerOpen={isRightDrawerOpen}
          portfolioData={portfolioData}
          portfolioGroupList={portfolioGroupList}
          portfolioMarkerColorList={portfolioMarkerColorList}
          formError={formError}
          onCancelForm={onCancelForm}
          onSubmitForm={onSubmitForm}
          onDealConnectEmailClick={dealConnectEmailClick}
        />
      ),
    },
  ];

  const onTabChange: Function = useCallback((newTabId: number): void => {
    const queryParams: LocationQueryParams = locationQueryParams || {};

    onCancelForm();

    tabs.forEach((tabItem: TabItem): void => {
      if (tabItem.id === newTabId) {
        queryParams[QUERY_PARAM_TAB] = tabItem.name;

        navigate({
          pathname: `${ROUTE_PORTFOLIO}/${portfolioId}`,
          search: FormatService.formatQueryParams(queryParams),
        });
      }
    });
  }, [locationQueryParams, tabs, portfolioId]);

  const onBeforeTabChange: Function = useCallback((newTabIndex: number): void => {
    if (isFormChanged) {
      setIsLeaveConfirmModalOpen(true);

      setLastTabIndex(newTabIndex);
    }
  }, [isFormChanged]);

  const onFormChange: Function = useCallback((event: React.SyntheticEvent): void => {
    const fieldName: string = event.target && (event.target as HTMLInputElement).name;

    if (withServerErrors && fieldName) {
      dispatch(resetFormErrors());
      FormService.resetFormServerErrors(formProps, fieldName);
      submitForm();
    }
  }, [withServerErrors, formProps]);

  useEffect((): void => {
    if (dealConnectorAuthLink) {
      dispatch(resetDealConnectorAuthLink());
      ScreenService.openLink(dealConnectorAuthLink);
    }
  }, [dealConnectorAuthLink]);

  useEffect((): void => {
    if (portfolioId) {
      dispatch(getPortfolioData({
        portfolioId,
      }));
    }
  }, [portfolioId]);

  useEffect((): void => {
    if (isLeaveConfirmed) {
      if (lastLocation) {
        navigate({
          pathname: lastLocation.pathname,
          search: lastLocation.search || '',
        });

        setLastLocation(undefined);
      }

      if (lastTabIndex !== null && lastTabIndex >= TAB_INDEX_DEFAULT) {
        const queryParams: LocationQueryParams = locationQueryParams || {};
        const currentTab: TabItem = tabs[lastTabIndex];

        if (currentTab) {
          queryParams[QUERY_PARAM_TAB] = currentTab.name;

          navigate({
            pathname: `${ROUTE_PORTFOLIO}/${portfolioId}`,
            search: FormatService.formatQueryParams(queryParams),
          });
        }

        setLastTabIndex(null);
      }

      setIsLeaveConfirmed(false);
    }
  }, [isLeaveConfirmed]);

  useEffect((): void => {
    if (lastLocation) {
      setLastTabIndex(null);
    }
  }, [lastLocation]);

  useServerFormErrors(serverFormErrors, (): void => {
    submitForm();
  });

  useEditModeQuery(isEditModeQueryDisabled);

  useFormBlock(onFormLeave, isFormChanged, isFormStateChanged, isLeaveConfirmed, isLeaveConfirmModalOpen);

  if (!portfolioId) {
    return <Navigate to={ROUTE_NOT_FOUND} />;
  }

  return (
    <CheckAccess permissions={portfolioEditPermissions}>
      <Form id={FORM_ID_SETTINGS_PORTFOLIO} onChange={onFormChange}>
        <PullToRefresh isDisabled={isEditMode} onRefresh={onRefresh}>
          <ContentHeader title={pageTitle} />
          <SettingsPageTabs
            isDisabled={isEditMode}
            isCompressed={isRightDrawerOpen}
            activeTab={TAB_NAME_PORTFOLIO_MANAGEMENT}
            currentUser={currentUser}
            onBeforeLocationChange={onBeforeLocationChange}
          />
          {
            !isDataLoaded && (
              <Loader isPageLoader withTopOffset />
            )
          }
          {
            isDataLoaded && (
              <Fragment>
                <Controls
                  isSaveDisabled={isSaveDisabled}
                  editPermissions={portfolioEditPermissions}
                  onCancelForm={onCancelForm}
                  onSubmitForm={onSubmitForm}
                />
                <PageTabs
                  isCompressed={isRightDrawerOpen}
                  isDisabled={isFormChanged}
                  tabs={tabs}
                  currentTab={queryTab}
                  onTabChange={onTabChange}
                  onBeforeTabChange={onBeforeTabChange}
                />
              </Fragment>
            )
          }
          {
            (!!formError || !!mainServerFormError) && !isFormSaving && (
              <FormError
                withShowError={isEditMode}
                withLeftOffset={isRightDrawerOpen}
                error={formError || mainServerFormError}
              />
            )
          }
        </PullToRefresh>
        <LeaveConfirmModal
          isOpen={isLeaveConfirmModalOpen}
          onConfirm={onLeaveConfirm}
          onCloseModal={closeLeaveConfirmModal}
        />
        <StatusBar isOpen={isOpenMessage} message={statusMessage} onClose={closeMessage} />
      </Form>
    </CheckAccess>
  );
});

const initialValues: FormValues = {};
const mapState: ReduxMapStateCallback = (reducers: Reducers): ReduxMapState => {
  const {
    app,
    controls,
    rightDrawer,
    settingsPortfolio,
    settingsPortfolioGroup,
  } = reducers;

  const portfolioData: Portfolio = settingsPortfolio && settingsPortfolio.portfolioData
    && settingsPortfolio.portfolioData.portfolio;

  if (portfolioData) {
    initialValues.id = portfolioData.id;
    initialValues.name = portfolioData.name;
    initialValues.capitalAllocated = FormatService.formatNumber(portfolioData.capitalAllocated);
    initialValues.groupId = (portfolioData.group && portfolioData.group.id) || null;
    initialValues.markerColor = portfolioData.markerColor ? portfolioData.markerColor.name : MARKER_COLOR_RED;
    initialValues.isHidden = portfolioData.isHidden || false;
    initialValues.dealConnectorEmail = portfolioData.dealConnectorEmail;
  }

  return {
    isDataLoaded: !!settingsPortfolio.portfolioData,
    isEditMode: controls.isEditMode,
    isRightDrawerOpen: rightDrawer.isDrawerOpen,
    currentUser: app.baseData && app.baseData.currentUser,
    portfolioData: settingsPortfolio.portfolioData
      && settingsPortfolio.portfolioData.portfolio,
    portfolioMarkerColorList: settingsPortfolio.portfolioData
      && settingsPortfolio.portfolioData.portfolioMarkerColorList,
    portfolioGroupList: settingsPortfolio.portfolioData
      && settingsPortfolio.portfolioData.portfolioGroupList,
    dealConnectorAuthLink: settingsPortfolioGroup.dealConnectorAuthLink,
  };
};

const SettingsPortfolioForm: ReactComponent = (props: ReactComponentProps): ReactElement => {
  const formErrors: ValidationErrors = useSelector(({ settingsPortfolio }: Reducers): ValidationErrorsMap => (
    settingsPortfolio.formErrors
  ));
  const isFormSaving: ValidationErrors = useSelector(({ settingsPortfolio }: Reducers): ValidationErrorsMap => (
    settingsPortfolio.isFormSaving
  ));

  return (
    <FormContainer
      formId={FORM_ID_SETTINGS_PORTFOLIO}
      component={SettingsPortfolio}
      validator={SettingsPortfolioValidator}
      initialValues={initialValues}
      formErrors={formErrors}
      isSubmitting={isFormSaving}
      {...props}
    />
  );
};

export default connect(mapState)(SettingsPortfolioForm);
